// 求人原稿生成(媒体別)の結果表示コンポーネント。
//
// 受け取る Markdown は構造化:
//   ## (媒体名) 向け原稿
//   ### [(媒体名)] (項目名) [補足]
//   (本文 / [要確認:〜] / > 法令注意:〜 / > ※〜)
//
// 機能:
// - 媒体ブロック(アコーディオン)+ 項目カード(コピーボタン+[要確認]/法令バッジ)
// - 媒体ごと一括コピー / 全体 Markdown コピー
// - プレビュー / 編集モード切替(textarea で生 Markdown を直編集)
// - フォーマット崩れ時は素の Markdown をフォールバック描画
import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface FieldItem {
  title: string;          // 例: "[Indeed] 職種名(タイトル) [30字以内]"
  bareTitle: string;      // 例: "職種名(タイトル) [30字以内]"(コピー用見出し)
  body: string;           // 項目の本文(法令注意・※注釈を含む生 Markdown)
  copyText: string;       // 入力欄にそのまま貼れる形(法令注意・※を除いたプレーンテキスト)
  hasNeedsConfirm: boolean;
  hasLegalNote: boolean;
  legalNotes: string[];
  styleNotes: string[];
}

interface MediaBlock {
  mediaLabel: string;     // "Indeed" / "gaten職" / "Engage"
  rawTitle: string;       // "Indeed 向け原稿" 等
  items: FieldItem[];
  needsConfirmCount: number;
  legalNoteCount: number;
}

/** Markdown を ## 単位で分割(コードフェンス内は無視)。 */
function splitByH2(md: string): Array<{ title: string; body: string }> {
  const lines = md.split('\n');
  const out: Array<{ title: string; body: string }> = [];
  let cur: { title: string; body: string } | null = null;
  const pre: string[] = [];
  let inFence = false;
  for (const line of lines) {
    if (/^\s*```/.test(line)) inFence = !inFence;
    const m = !inFence ? line.match(/^##\s+(.+?)\s*$/) : null;
    if (m) {
      if (cur) out.push(cur);
      cur = { title: m[1], body: '' };
    } else if (cur) {
      cur.body += line + '\n';
    } else {
      pre.push(line);
    }
  }
  if (cur) out.push(cur);
  const preamble = pre.join('\n').trim();
  if (preamble) out.unshift({ title: '', body: preamble });
  return out;
}

/** ## の本文を ### 単位で分割(コードフェンス内は無視)。 */
function splitByH3(md: string): Array<{ title: string; body: string }> {
  const lines = md.split('\n');
  const out: Array<{ title: string; body: string }> = [];
  let cur: { title: string; body: string } | null = null;
  const pre: string[] = [];
  let inFence = false;
  for (const line of lines) {
    if (/^\s*```/.test(line)) inFence = !inFence;
    const m = !inFence ? line.match(/^###\s+(.+?)\s*$/) : null;
    if (m) {
      if (cur) out.push(cur);
      cur = { title: m[1], body: '' };
    } else if (cur) {
      cur.body += line + '\n';
    } else {
      pre.push(line);
    }
  }
  if (cur) out.push(cur);
  const preamble = pre.join('\n').trim();
  if (preamble) out.unshift({ title: '', body: preamble });
  return out;
}

/** 媒体名を見出しから抽出。"Indeed 向け原稿" → "Indeed"。 */
function extractMediaLabel(h2Title: string): string {
  const m = h2Title.match(/^(.+?)\s*向け原稿\s*$/);
  return m ? m[1].trim() : h2Title;
}

/** 項目見出し "[Indeed] 項目名" の媒体プレフィックスを剥がす。 */
function stripMediaPrefix(h3Title: string): string {
  return h3Title.replace(/^\[[^\]]+\]\s*/, '').trim();
}

/** 項目本文を 本文 / 法令注意 / ※スタイル注意 に分離(コピー用テキストを作るため)。 */
function partitionBody(body: string): { mainLines: string[]; legalNotes: string[]; styleNotes: string[] } {
  const mainLines: string[] = [];
  const legalNotes: string[] = [];
  const styleNotes: string[] = [];
  for (const rawLine of body.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    const legal = line.match(/^>\s*法令注意[::]\s*(.+)$/);
    const style = line.match(/^>\s*※\s*(.+)$/);
    if (legal) {
      legalNotes.push(legal[1].trim());
    } else if (style) {
      styleNotes.push(style[1].trim());
    } else if (/^>\s*$/.test(line)) {
      // 空の引用行は捨てる
    } else {
      mainLines.push(line);
    }
  }
  return { mainLines, legalNotes, styleNotes };
}

/** Markdown 記号を軽く除去(媒体貼り付け用プレーンテキスト)。 */
function toPlainText(md: string): string {
  return md
    .replace(/```[a-zA-Z]*\n?/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^\s*[*-]\s+/gm, '・')
    .replace(/^\s*(\d+)\.\s+/gm, '$1. ')
    .replace(/^---+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** [要確認:〜] を含むか。 */
function hasNeedsConfirmTag(text: string): boolean {
  return /\[\s*要確認[::]/.test(text);
}

/** 構造化 Markdown → MediaBlock[] にパース。 */
function parseResult(md: string): { blocks: MediaBlock[]; preamble: string } {
  const h2s = splitByH2(md);
  let preamble = '';
  const blocks: MediaBlock[] = [];

  for (const sec of h2s) {
    if (sec.title === '') {
      preamble = sec.body.trim();
      continue;
    }
    const mediaLabel = extractMediaLabel(sec.title);
    const h3s = splitByH3(sec.body);
    const items: FieldItem[] = [];
    let blockPreamble = '';
    for (const sub of h3s) {
      if (sub.title === '') {
        blockPreamble = sub.body.trim();
        continue;
      }
      const bareTitle = stripMediaPrefix(sub.title);
      const { mainLines, legalNotes, styleNotes } = partitionBody(sub.body);
      const mainText = mainLines.join('\n').trim();
      const hasConfirm = hasNeedsConfirmTag(mainText) || mainLines.some((l) => hasNeedsConfirmTag(l));
      items.push({
        title: sub.title,
        bareTitle,
        body: sub.body.trim(),
        copyText: toPlainText(mainText),
        hasNeedsConfirm: hasConfirm,
        hasLegalNote: legalNotes.length > 0,
        legalNotes,
        styleNotes,
      });
    }
    blocks.push({
      mediaLabel,
      rawTitle: sec.title,
      items,
      needsConfirmCount: items.filter((i) => i.hasNeedsConfirm).length,
      legalNoteCount: items.reduce((acc, i) => acc + (i.hasLegalNote ? 1 : 0), 0),
    });
    if (blockPreamble) {
      // ブロック先頭の前置きは捨てない:最初の項目の前に挿入されるように後で表示する
      // ここでは items に擬似項目として追加せず、必要ならコメントとして残す
      // (実用上モデルが前置きを出さないことが多い)
    }
  }

  return { blocks, preamble };
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({
  text,
  label = 'コピー',
  variant = 'secondary',
}: {
  text: string;
  label?: string;
  variant?: 'primary' | 'secondary';
}) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  const cls = variant === 'primary' ? 'btn-primary' : 'btn-secondary';
  return (
    <button type="button" onClick={onClick} className={`${cls} !px-3 !py-1 text-xs`}>
      {copied ? '✓ コピーしました' : label}
    </button>
  );
}

/** 本文中の `[要確認:〜]` を視覚的に赤バッジで表示 */
function HighlightedBody({ text }: { text: string }) {
  if (!text.trim()) return null;
  // [要確認:〜] を分割しつつ間にプレーンテキストを残す
  const parts: Array<{ kind: 'text' | 'flag'; value: string }> = [];
  const re = /\[\s*要確認[::]\s*([^\]]+?)\s*\]/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push({ kind: 'text', value: text.slice(lastIndex, m.index) });
    parts.push({ kind: 'flag', value: m[1] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push({ kind: 'text', value: text.slice(lastIndex) });

  return (
    <div className="space-y-1.5 text-sm leading-relaxed text-slate-800">
      {parts.map((p, i) =>
        p.kind === 'flag' ? (
          <span
            key={i}
            className="mr-1 inline-flex items-center gap-1 rounded border border-rank-1 bg-rank-1/10 px-2 py-0.5 text-xs font-semibold text-rank-1"
          >
            <span aria-hidden>⚠</span>要確認:{p.value}
          </span>
        ) : (
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <span className="whitespace-pre-wrap">{children}</span>,
              ul: ({ children }) => <ul className="my-1 ml-4 list-disc">{children}</ul>,
              ol: ({ children }) => <ol className="my-1 ml-4 list-decimal">{children}</ol>,
              li: ({ children }) => <li>{children}</li>,
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            }}
          >
            {p.value}
          </ReactMarkdown>
        ),
      )}
    </div>
  );
}

function FieldCard({ item }: { item: FieldItem }) {
  const { mainLines } = partitionBody(item.body);
  const mainText = mainLines.join('\n').trim();
  return (
    <div
      className={`overflow-hidden rounded-lg border bg-white ${
        item.hasNeedsConfirm ? 'border-rank-1/60' : 'border-slate-200'
      }`}
    >
      <div className="flex items-start justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-semibold text-slate-800">{item.bareTitle}</span>
          {item.hasNeedsConfirm && (
            <span className="inline-flex items-center gap-1 rounded bg-rank-1/10 px-1.5 py-0.5 text-[11px] font-bold text-rank-1">
              要確認
            </span>
          )}
          {item.hasLegalNote && (
            <span className="inline-flex items-center gap-1 rounded bg-rank-2/10 px-1.5 py-0.5 text-[11px] font-bold text-rank-2">
              法令注意
            </span>
          )}
        </div>
        <CopyButton text={item.copyText || mainText} label="この項目をコピー" />
      </div>
      <div className="space-y-2 px-3 py-2.5">
        {mainText ? (
          <HighlightedBody text={mainText} />
        ) : (
          <p className="text-xs italic text-slate-400">(本文なし)</p>
        )}
        {item.legalNotes.map((note, i) => (
          <div
            key={`legal-${i}`}
            className="flex items-start gap-1.5 rounded border border-rank-2/40 bg-rank-2/5 px-2 py-1.5 text-xs text-rank-2"
          >
            <span className="shrink-0 font-bold" aria-hidden>
              ⚠
            </span>
            <span>
              <strong className="font-semibold">法令注意:</strong>
              {note}
            </span>
          </div>
        ))}
        {item.styleNotes.map((note, i) => (
          <div
            key={`style-${i}`}
            className="flex items-start gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-500"
          >
            <span className="shrink-0" aria-hidden>
              ※
            </span>
            <span>{note}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MediaBlockCard({ block, defaultOpen }: { block: MediaBlock; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const allText = useMemo(
    () =>
      block.items
        .map((i) => {
          const main = partitionBody(i.body).mainLines.join('\n').trim();
          return `■ ${i.bareTitle}\n${toPlainText(main)}`;
        })
        .join('\n\n'),
    [block],
  );
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 bg-brand/5 px-4 py-3 text-left hover:bg-brand/10"
      >
        <div className="flex items-center gap-3">
          <span className="text-base font-bold text-slate-800">{block.mediaLabel} 向け原稿</span>
          <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
            項目 {block.items.length}
          </span>
          {block.needsConfirmCount > 0 && (
            <span className="rounded bg-rank-1/10 px-2 py-0.5 text-[11px] font-bold text-rank-1">
              要確認 {block.needsConfirmCount}
            </span>
          )}
          {block.legalNoteCount > 0 && (
            <span className="rounded bg-rank-2/10 px-2 py-0.5 text-[11px] font-bold text-rank-2">
              法令注意 {block.legalNoteCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {open && (
            <span onClick={(e) => e.stopPropagation()}>
              <CopyButton text={allText} label="この媒体の全項目をコピー" />
            </span>
          )}
          <span className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        </div>
      </button>
      {open && (
        <div className="space-y-2 px-3 py-3">
          {block.items.length === 0 ? (
            <p className="text-sm italic text-slate-400">(項目が抽出できませんでした。編集モードで生 Markdown を確認してください)</p>
          ) : (
            block.items.map((item, i) => <FieldCard key={i} item={item} />)
          )}
        </div>
      )}
    </div>
  );
}

export function AdGenResult({
  markdown,
  onChange,
}: {
  markdown: string;
  onChange: (v: string) => void;
}) {
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const { blocks, preamble } = useMemo(() => parseResult(markdown), [markdown]);
  const totalNeedsConfirm = blocks.reduce((acc, b) => acc + b.needsConfirmCount, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-md border border-slate-300 p-0.5">
          {(['preview', 'edit'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded px-3 py-1 text-xs font-medium transition ${
                mode === m ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {m === 'preview' ? '整形プレビュー' : '編集'}
            </button>
          ))}
        </div>
        <CopyButton text={markdown} label="全体をコピー(Markdown)" />
      </div>

      {mode === 'edit' ? (
        <div className="space-y-2">
          <textarea
            className="input min-h-[600px] resize-y whitespace-pre-wrap font-mono text-xs leading-relaxed"
            value={markdown}
            onChange={(e) => onChange(e.target.value)}
            aria-label="生成結果(編集)"
          />
          <div className="flex justify-end">
            <button onClick={() => setMode('preview')} className="btn-primary !py-1.5 text-xs">
              編集を確定してプレビューに戻る
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {preamble && (
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{preamble}</ReactMarkdown>
            </div>
          )}
          {totalNeedsConfirm > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-rank-1/50 bg-rank-1/5 px-3 py-2 text-sm text-rank-1">
              <span className="shrink-0 font-bold" aria-hidden>
                ⚠
              </span>
              <span>
                <strong className="font-semibold">[要確認] が {totalNeedsConfirm} 件</strong> あります。
                クライアントにヒアリングしてから入稿してください(推測で埋めない設計)。
              </span>
            </div>
          )}
          {blocks.length === 0 ? (
            <div className="card text-sm text-slate-500">
              <p className="mb-2">媒体ブロック(## ◯◯ 向け原稿)が見つかりませんでした。生 Markdown を表示します。</p>
              <pre className="whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 font-mono text-xs leading-relaxed text-slate-600">
                {markdown}
              </pre>
            </div>
          ) : (
            blocks.map((block, i) => (
              <MediaBlockCard key={i} block={block} defaultOpen={blocks.length <= 1 || i === 0} />
            ))
          )}
        </div>
      )}

      <p className="text-xs text-slate-400">
        ※ 生成結果はそのまま入稿せず、担当者が内容を確認・編集してから使用してください。特に [要確認] 項目はクライアントへのヒアリングが必要です。
      </p>
    </div>
  );
}
